import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { toast } from 'sonner';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { Button } from '@/components/ui/button';
import { useSpecimenFilterField } from '@/features/product/hooks/useSpecimenFilterField';
import {
  requisitionQueryKeys,
  requisitionSheetQueryOptions,
  saveRequisitionSheet,
} from '../api/productRequisition.api';
import { RequisitionFilterPanel } from '../components/RequisitionFilterPanel';
import { RequisitionSheetTable } from '../components/RequisitionSheetTable';
import { useSheetDraft } from '../hooks/useSheetDraft';
import {
  toDraftLine,
  toQty,
  toSheetDraft,
  type SheetDraft,
  type SheetDraftLine,
} from '../utils/sheetDrafts';
import { useRequisitionFilters } from '../hooks/useRequisitionFilters';
import type { RequisitionSheetSearch } from '../types/productRequisition.types';
import { Can } from '@/components/common/Can';
import { PRODUCT_PERMISSIONS } from '@/constants/permissions';

const route = getRouteApi('/_auth/product/specimen-requisition');

const NUMERIC_KEYS = [
  'specimenId',
  'bookTypeId',
  'bookEditionId',
  'instituteTypeId',
  'thanaId',
] as const;

export function SpecimenRequisitionPage() {
  const queryClient = useQueryClient();
  const routeSearch = route.useSearch();
  const routeNavigate = route.useNavigate();
  const controller = useRequisitionFilters({
    search: routeSearch,
    numericKeys: NUMERIC_KEYS,
    onApplyFilters: (next) =>
      void routeNavigate({ search: (previous) => ({ ...previous, ...next }), replace: true }),
  });
  const params = controller.params as RequisitionSheetSearch;

  const { data, isLoading, error, refetch } = useQuery(requisitionSheetQueryOptions(params));

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const specimenField = useSpecimenFilterField(controller.filterValues.specimenId ?? '');
  const [draft, setDraft] = useSheetDraft<typeof data, SheetDraft>(
    data,
    (sheet) => toSheetDraft(sheet?.institutes ?? []),
    {},
  );

  const mutation = useMutation({
    mutationFn: async () => {
      if (params.specimenId === undefined) return;
      const lines = (data?.institutes ?? [])
        .filter((row) => !row.isReview)
        .map((row) => {
          const line = draft[row.instituteId] ?? toDraftLine(row);
          return {
            instituteId: row.instituteId,
            requiredQty: toQty(line.requiredQty),
            teacherQty: toQty(line.teacherQty),
            studentQty: toQty(line.studentQty),
            targetSaleQty: toQty(line.targetSaleQty),
            ...(line.targetDate === '' ? {} : { targetDate: line.targetDate }),
          };
        });

      if (lines.length === 0) throw new Error('Nothing to save');
      return saveRequisitionSheet(params.specimenId, lines);
    },
    onSuccess: (saved) => {
      const locked = saved?.locked ?? 0;
      toast.success(
        locked > 0
          ? `Saved ${saved?.stored ?? 0}; ${locked} already reviewed and left alone`
          : `Saved ${saved?.stored ?? 0} institute(s)`,
      );
      void queryClient.invalidateQueries({ queryKey: requisitionQueryKeys.all() });
    },
    onError: () => toast.error('Could not save the sheet'),
  });

  const onLineChange = (instituteId: number, line: SheetDraftLine) =>
    setDraft((previous) => ({ ...previous, [instituteId]: line }));

  const draftTotals = useMemo(() => {
    const lines = Object.values(draft);
    return {
      required: lines.reduce((sum, line) => sum + toQty(line.requiredQty), 0),
      teacher: lines.reduce((sum, line) => sum + toQty(line.teacherQty), 0),
      targetSale: lines.reduce((sum, line) => sum + toQty(line.targetSaleQty), 0),
    };
  }, [draft]);

  const stats = [
    { key: 'institutes', label: 'Institutes', value: data?.institutes.length },
    { key: 'required', label: 'Requisition Qty', value: draftTotals.required },
    { key: 'teacher', label: 'Teacher Qty', value: draftTotals.teacher },
    { key: 'targetSale', label: 'Target Sale Qty', value: draftTotals.targetSale },
  ];

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Specimen Requisition"
        actions={
          <>
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={controller.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
            <Can code={PRODUCT_PERMISSIONS.SPECIMEN_REQUISITION_EDIT}>
              <Button
                type="button"
                onClick={() => mutation.mutate()}
                disabled={
                  mutation.isPending || params.specimenId === undefined || data === undefined
                }
              >
                Save Sheet
              </Button>
            </Can>
          </>
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <RequisitionFilterPanel
          filters={controller}
          pageFields={[specimenField]}
          withGeo
          withInstituteType
          withBook
        />
      </FilterPanelCollapse>

      {params.specimenId === undefined ? (
        <EmptyState
          title="Pick a specimen"
          description="Choose a book type, edition and specimen to open the sheet for your institutes."
        />
      ) : (
        <>
          <StatCardGrid stats={stats} isLoading={isLoading} isError={error !== null} />

          {error ? (
            <ErrorState
              error={error}
              fallbackTitle="Could not load the sheet"
              onRetry={() => void refetch()}
              fill
            />
          ) : isLoading ? (
            <ListSkeleton />
          ) : data === undefined || data.institutes.length === 0 ? (
            <EmptyState
              title="No institute to sheet"
              description="No institute is assigned to you under this type and thana."
            />
          ) : (
            <RequisitionSheetTable
              institutes={data.institutes}
              draft={draft}
              onChange={onLineChange}
            />
          )}
        </>
      )}
    </div>
  );
}
